import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useEpcProjectDetail } from '../project/EpcProjectDetailContext';
import { useActiveOrgUnit, type MyOrgUnit } from './hooks/useActiveOrgUnit';

interface Ctx {
  projectId: string;
  myOrgUnits: MyOrgUnit[];
  activeOrgUnitId: string | null;
  setActiveOrgUnit: (id: string) => void;
  engineersInActiveOrgUnit: { userId: string; userName: string }[];
}

const DmCtx = createContext<Ctx | null>(null);

export function EpcDmProjectDetailProvider({ children }: { children: ReactNode }) {
  const detail = useEpcProjectDetail();
  // EpcProject is normalized to `_id`; fall back to `id` if present on the raw object.
  const projectId = detail.project?._id ?? detail.id ?? '';
  const myUserId = detail.myUserId ?? '';
  const myCap = detail.myCap;

  const myOrgUnits = useMemo<MyOrgUnit[]>(() => {
    const seen = new Map<string, string>();
    // Role that grants this user a "Planning"/management surface on an org unit:
    // - dept_head sees all the org units they head
    // - manager sees all the org units they manage
    const matchRole = myCap === 'dept_head' ? 'department_head' : 'manager';
    for (const m of detail.memberships) {
      if (!m.inactive && m.role === matchRole && m.userId === myUserId && m.orgUnitId) {
        if (!seen.has(m.orgUnitId)) {
          seen.set(m.orgUnitId, m.orgUnitName ?? m.orgUnitId);
        }
      }
    }
    return Array.from(seen.entries()).map(([orgUnitId, orgUnitName]) => ({
      orgUnitId,
      orgUnitName,
    }));
  }, [detail.memberships, myUserId, myCap]);

  const { activeOrgUnitId, setActiveOrgUnit } = useActiveOrgUnit(myOrgUnits);

  const engineersInActiveOrgUnit = useMemo(() => {
    if (!activeOrgUnitId) return [];
    const map = new Map<string, { userId: string; userName: string }>();
    for (const m of detail.memberships) {
      if (m.inactive || m.role !== 'site_engineer' || m.orgUnitId !== activeOrgUnitId) continue;
      // DM scopes to their direct reports; DH sees all engineers in the org unit.
      if (myCap === 'manager' && m.reportsToUserId !== myUserId) continue;
      if (!map.has(m.userId)) {
        map.set(m.userId, { userId: m.userId, userName: m.userName ?? m.userId });
      }
    }
    return Array.from(map.values());
  }, [detail.memberships, activeOrgUnitId, myUserId, myCap]);

  const value: Ctx = {
    projectId,
    myOrgUnits,
    activeOrgUnitId,
    setActiveOrgUnit,
    engineersInActiveOrgUnit,
  };
  return <DmCtx.Provider value={value}>{children}</DmCtx.Provider>;
}

export function useDmProjectDetail(): Ctx {
  const ctx = useContext(DmCtx);
  if (!ctx) {
    throw new Error('useDmProjectDetail must be used inside EpcDmProjectDetailProvider');
  }
  return ctx;
}

/**
 * Non-throwing variant for shared components that may render outside the DM
 * provider tree (e.g. report buttons reused by DH/PH/DM reports tabs).
 */
export function useDmProjectDetailOptional(): Ctx | null {
  return useContext(DmCtx);
}
